import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db, auth, functions } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { usePlan } from '../contexts/PlanContext';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshPlan } = usePlan();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const processPayment = async () => {
      try {
        const sessionId = searchParams.get('session_id');
        if (!sessionId) {
          navigate('/');
          return;
        }

        // Wait briefly for Stripe to finalize
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Sync subscription from Stripe to Firestore
        setStatus('syncing');
        let expectedRole = null;
        try {
          const syncFn = httpsCallable(functions, 'sync_subscription');
          const syncResult = await syncFn();
          expectedRole = syncResult.data?.role || null;
        } catch (syncErr) {
          console.error('Sync subscription error (non-fatal):', syncErr);
        }

        // Poll until stripeRole claim matches the synced role (max 15s)
        if (auth.currentUser) {
          const maxAttempts = 15;
          for (let i = 0; i < maxAttempts; i++) {
            await auth.currentUser.getIdToken(true);
            const { claims } = await auth.currentUser.getIdTokenResult();
            const currentRole = claims.stripeRole || 'free';
            // If we know the expected role, wait for it specifically
            // Otherwise just wait for anything other than 'free'
            if (expectedRole) {
              if (currentRole === expectedRole) break;
            } else {
              if (currentRole !== 'free') break;
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        // Refresh plan context so all pages see the updated plan
        await refreshPlan();

        setStatus('success');
        await new Promise(resolve => setTimeout(resolve, 1500));
        navigate('/profile');
      } catch (error) {
        console.error('Error processing payment:', error);
        setStatus('error');
        setTimeout(() => navigate('/'), 3000);
      }
    };

    processPayment();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-lg p-10 text-center max-w-md w-full border border-purple-100"
      >
        {status === 'error' ? (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
              <span className="text-3xl">!</span>
            </div>
            <h1 className="text-xl font-bold text-gray-800 mb-2">Algo deu errado</h1>
            <p className="text-gray-500">Redirecionando...</p>
          </>
        ) : status === 'success' ? (
          <>
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
              <span className="text-3xl">&#10003;</span>
            </div>
            <h1 className="text-xl font-bold text-gray-800 mb-2">Pagamento confirmado!</h1>
            <p className="text-gray-500">Redirecionando para seu perfil...</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-5 animate-pulse">
              <Sparkles className="w-8 h-8 text-purple-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-800 mb-2">
              {status === 'syncing' ? 'Ativando seu plano...' : 'Processando pagamento...'}
            </h1>
            <p className="text-gray-500">Por favor, aguarde um momento.</p>
            <div className="mt-6 flex justify-center">
              <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default SuccessPage;
